import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { createResource, useResource } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
// eslint-disable-next-line jira/import-whitelist
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { QUERY } from './gql.tsx';
import type { RovoEntitlementGraphQLResponse } from './types.tsx';

const RESOURCE_TYPE_ROVO_ENTITLEMENT = 'ROVO_ENTITLEMENT';

type RovoEntitlementData = {
	isRovoEnabled: boolean | null;
	isRovoLLMEnabled: boolean | null | undefined;
};

type ExperimentCohorts = 'control' | 'test';

const getRovoEnabledBasedOnExperiment = ({
	isRovoEnabled,
	isRovoLLMEnabled,
}: {
	isRovoEnabled: boolean | null;
	isRovoLLMEnabled: boolean | null;
}) => {
	// if Rovo is not enabled, we don't need to check the experiment
	if (!isRovoEnabled) {
		return { isRovoEnabled, isRovoLLMEnabled };
	}

	// Killswitch gate to disable the experiment
	if (!fg('rovo_team25_rollout_performance_exp_enabled')) {
		return { isRovoEnabled, isRovoLLMEnabled };
	}

	// If a user is in the Control cohort we're overriding the entitlement check with false to NOT serve Rovo experience.
	// Test cohort: users who get the new Rovo experience -> do NOT override the TCS entitlement.
	// Control cohort: users who get the legacy experience (the baseline) -> override the TCS entitlement with false.
	if (
		expValEquals<ExperimentCohorts>(
			'rovo_team25_rollout_performance_exp',
			'cohort',
			'control',
			'test',
		)
	) {
		return { isRovoEnabled: false, isRovoLLMEnabled: false };
	}

	return { isRovoEnabled, isRovoLLMEnabled };
};

const fetchRovoEntitlement = async (cloudId: string): Promise<RovoEntitlementData> => {
	try {
		const response = await performPostRequest<RovoEntitlementGraphQLResponse>(
			'/gateway/api/graphql',
			{
				body: JSON.stringify({
					query: QUERY,
					variables: {
						cloudId,
						shouldShowRovoLLMEnabled: fg('rovo_use_rovo_llm_enabled_check'),
					},
				}),
				headers: {
					'content-type': 'application/json',
				},
			},
		);

		// handle GraphQL errors
		if (response.errors) {
			fireErrorAnalytics({
				meta: {
					id: 'navigationResource',
					packageName: 'jiraRouterResourcesRovoEntitlement',
					teamName: 'ai-mate',
				},
				error: new Error(
					`RovoEntitlement Error. ${response.errors.map((error) => error.message).join('; ')}; IsDataEmpty=${response.data?.jira === null || response.data?.jira === undefined}`,
				),
				sendToPrivacyUnsafeSplunk: true,
			});
		}

		const isRovoEnabled = response.data?.jira?.isRovoEnabled ?? null;
		// defaulting to isRovoEnabled if the feature flag is not enabled
		const isRovoLLMEnabled = fg('rovo_use_rovo_llm_enabled_check')
			? response.data?.jira_isRovoLLMEnabled ?? null
			: isRovoEnabled;

		return getRovoEnabledBasedOnExperiment({
			isRovoEnabled,
			isRovoLLMEnabled,
		});
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			meta: {
				id: 'navigationResource',
				packageName: 'jiraRouterResourcesRovoEntitlement',
				teamName: 'ai-mate',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
	}

	return {
		isRovoEnabled: null,
		isRovoLLMEnabled: undefined,
	};
};

export const rovoEntitlementResource = createResource<RovoEntitlementData>({
	type: RESOURCE_TYPE_ROVO_ENTITLEMENT,
	getKey: () => RESOURCE_TYPE_ROVO_ENTITLEMENT,
	getData: (_, { tenantContext: { cloudId } }) => fetchRovoEntitlement(cloudId),
	maxAge: Infinity,
});

export const useRovoEntitlementResource = () => useResource(rovoEntitlementResource);
